/**
 * Affiliate attribution for the Trading Competition page (EN and DE).
 *
 * Partners share `/trading-competition?aff=CODE`. The code is sanitized, remembered in localStorage
 * so it survives a reload or a later visit, and appended as `?aff=CODE` to every CTA that points at
 * the checkout. A new code in the URL replaces the remembered one.
 *
 * The storage key is page-specific on purpose. `lib/utm.ts` reads a global `aff` key with
 * "first contact wins" and drops `coupon` whenever `aff` is present, so writing there would change
 * the checkout CTAs of every other page for anyone who once opened a competition partner link.
 */
export const TC_AFF_STORAGE_KEY = "tradingCompetitionAff";
export const AFF_CODE_MAX_LENGTH = 50;
const CHECKOUT_HOST = "checkout.iqcapital.io";

/** Letters, digits, underscore and hyphen only, at most 50 characters. Anything else is dropped. */
export function sanitizeAffCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, AFF_CODE_MAX_LENGTH);
  return code || null;
}

/**
 * The code to attribute this page view to. Browser only.
 *
 * `code`: the URL's `?aff=` when it is present and valid (it is remembered, replacing an older one),
 * otherwise the remembered code, otherwise null. `urlHasAff` tells whether the URL carried an `aff`
 * at all, valid or not, so an invalid one can be kept out of the checkout links.
 */
export function readAffCode(): { code: string | null; urlHasAff: boolean } {
  if (typeof window === "undefined") return { code: null, urlHasAff: false };

  const rawUrlAff = new URLSearchParams(window.location.search).get("aff");
  const urlHasAff = rawUrlAff !== null;
  const fromUrl = sanitizeAffCode(rawUrlAff);

  try {
    if (fromUrl) {
      localStorage.setItem(TC_AFF_STORAGE_KEY, fromUrl);
      return { code: fromUrl, urlHasAff };
    }
    return { code: sanitizeAffCode(localStorage.getItem(TC_AFF_STORAGE_KEY)), urlHasAff };
  } catch {
    // Private mode / blocked storage: the code still applies to this page view.
    return { code: fromUrl, urlHasAff };
  }
}

/**
 * Sets `aff` on a checkout link, or removes it when `code` is null. Hash anchors, site paths and
 * links to any other host come back unchanged.
 */
export function withCheckoutAffCode(href: string, code: string | null): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  if (url.hostname !== CHECKOUT_HOST) return href;
  if (code) url.searchParams.set("aff", code);
  else url.searchParams.delete("aff");
  return url.toString();
}
